import { useEffect, useRef, useState, type ReactNode } from 'react';
import { registerSW } from 'virtual:pwa-register';

/**
 * Meldet, wenn eine neue Fassung bereitsteht — und lädt sie auf Tastendruck.
 *
 * Warum es das braucht: Eine installierte Homescreen-App startet auf iOS meist aus dem
 * Ruhezustand, nicht neu. Der Service Worker fragt dann gar nicht nach einer neuen
 * Fassung, und die App bleibt auf altem Stand, während der Server längst aktualisiert
 * ist. Genau daran ist eine Fehlersuche schon einmal hängengeblieben („ich sehe die
 * Funktion nicht" — sie war da, nur nicht auf dem Telefon).
 *
 * Deshalb zwei Auslöser für die Nachfrage: einmal stündlich und jedes Mal, wenn die App
 * wieder sichtbar wird. Letzteres ist der Fall, der auf dem Telefon zählt.
 */
export function UpdateBanner(): ReactNode {
  const [bereit, setBereit] = useState(false);
  // Die Aktualisierungsfunktion ist kein Zustand, sondern ein Werkzeug — sie in
  // `useState` zu legen, löste eine überflüssige zweite Darstellung aus.
  const aktualisieren = useRef<(() => void) | null>(null);

  useEffect(() => {
    let aufraeumen: (() => void) | undefined;

    // `registerSW` liefert eine Funktion, die ein Promise zurueckgibt. Fuer den Knopf
    // interessiert der Ausgang nicht — die Seite laedt ohnehin neu.
    const neuladen = registerSW({
      immediate: true,
      onNeedRefresh() {
        setBereit(true);
      },
      onRegisteredSW(_url, registration) {
        if (registration === undefined) return;

        const pruefen = (): void => {
          void registration.update();
        };
        const beiSichtbarkeit = (): void => {
          if (document.visibilityState === 'visible') pruefen();
        };

        document.addEventListener('visibilitychange', beiSichtbarkeit);
        const timer = window.setInterval(pruefen, 60 * 60 * 1000);
        aufraeumen = () => {
          document.removeEventListener('visibilitychange', beiSichtbarkeit);
          window.clearInterval(timer);
        };
      },
    });

    aktualisieren.current = () => {
      void neuladen();
    };

    return () => aufraeumen?.();
  }, []);

  if (!bereit) return null;

  return (
    <div
      className="glass fixed inset-x-3 z-40 mx-auto flex max-w-[26rem] items-center gap-3 rounded-2xl px-4 py-3 ring-1 ring-white/15"
      style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
      role="status"
    >
      <p className="flex-1 text-[14px]">Neue Fassung verfügbar.</p>
      <button
        onClick={() => aktualisieren.current?.()}
        className="press rounded-xl bg-white px-3 py-2 text-[14px] font-semibold text-black"
      >
        Neu laden
      </button>
    </div>
  );
}
